import { fullNamePresenter } from 'utils';
import { useMemo, type FC } from 'react';
import {
  useAddActivityComment,
  useGenAiComment,
  type TRpmDashboardActivityQueryKey,
  type TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import useUser from 'utils/useUser';
import { useAppSelector } from 'store';
import { isCombinedModule } from 'infrastructure/functions';
import { useCheckRoleAccess } from 'infrastructure/hooks';

import DashboardReadingColumn from './components/dashboard-reading-col';
import DateColumnMobile from './components/date-col/mobile';
import ReviewedCheckbox from './components/reviewed-checkbox';
import PatientReadingColumn from './components/patient-reading-col';
import CommentsColumnMobile from './components/comments-col/mobile';
import CommentColumn from './components/comment-col';
import s from './styles.module.scss';
import MobileActionsRow from './mobile-actions-row';

import type { TView } from './types';
import type { ModuleType } from 'infrastructure/enums';
import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface IMobileCellProps {
  createdAt: string;
  record: IActivityData;
  idx: number | string;
  showReviewCol: boolean;
  view: TView;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
  hideCommentsColumn: boolean;
  module: ModuleType;
}

const MobileCell: FC<IMobileCellProps> = ({
  createdAt,
  idx,
  record,
  getActivityQueryKey,
  showReviewCol,
  view,
  hideCommentsColumn,
  module,
}) => {
  const [_, { page, items }] = getActivityQueryKey;
  const currentPage = page ?? 1;
  const itemIndexPrefix = (currentPage - 1) * (items ?? 100);
  const { userType, hasClinicalRole } = useUser();
  const { addActivityComment, InteractionStatusAlert } = useAddActivityComment({
    getActivityQueryKey,
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });
  const {
    data: aiData,
    genAiComment,
    isCommentGenerationLimitReached,
    isLoading,
  } = useGenAiComment({
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });
  const { isReadOnlyRole } = useCheckRoleAccess();
  const actions = useAppSelector((state) => state.activityActions.actions[idx]);
  const isReviewed = useMemo(() => {
    const reviewedByUserType = record.comments.find(
      (c) => c.reviewed,
    )?.userType;
    return reviewedByUserType === userType && record.reviewed;
  }, [record.comments.length]);

  const submitCommentLockByAi =
    Boolean(actions?.message) &&
    aiData?.result.comment === actions?.message &&
    hasClinicalRole;

  const dateColIdx =
    typeof idx === 'number' ? idx : Number.parseInt(idx.split('-')[1]) ?? 1;

  const showCommentColumn = isCombinedModule(module)
    ? !isReadOnlyRole
    : !hideCommentsColumn;

  return (
    <div className={s['mob-wrapper']}>
      <DateColumnMobile
        timezone={record.patient.timezone}
        createdAt={createdAt}
        globalStatus={record.globalStatus}
        idx={itemIndexPrefix + dateColIdx + 1}
        thresholds={record.patient.threshold}
        thresholdName={record.thresholdName}
        mightBeErroneousReason={record.mightBeErroneousReason}
        readingType={record.readingType}
      >
        {showReviewCol ? (
          <div className={s.reviewed} data-view="mobile">
            <ReviewedCheckbox
              key={record.id + record.deviceId}
              addComment={addActivityComment}
              deviceId={record.deviceId}
              readingId={record.id}
              idx={idx}
              currentInteractionStatus={record.patient.interactionStatus}
              isReviewed={isReviewed}
              submitCommentLockByAi={submitCommentLockByAi}
            />
          </div>
        ) : (
          <></>
        )}
      </DateColumnMobile>
      {showReviewCol && view !== 'dashboard' && (
        <MobileActionsRow
          record={record}
          getActivityQueryKey={getActivityQueryKey}
          view={view}
        />
      )}
      {view === 'dashboard' && (
        <div style={{ position: 'relative' }}>
          <DashboardReadingColumn
            globalStatus={record.globalStatus}
            readingType={record.readingType}
            statuses={record.statuses}
            thresholds={record.patient.threshold}
            values={record.value}
            key={`reading-col-${idx}`}
            patient={record.patient}
            provider={fullNamePresenter(record.doctor)}
            reserved={record.reserved}
            getActivityQueryKey={getActivityQueryKey}
            deviceId={record.deviceId}
            readingId={record.id}
            hideCommentsColumn={hideCommentsColumn}
          />
        </div>
      )}
      <PatientReadingColumn
        globalStatus={record.globalStatus}
        readingType={record.readingType}
        statuses={record.statuses}
        thresholds={record.patient.threshold}
        values={record.value}
        key={`reading-col-${idx}`}
        thresholdName={record.thresholdName}
        patientGuid={record.patient.guid}
        isShowAssignedToggle={showReviewCol}
      />
      {!hideCommentsColumn && (
        <CommentsColumnMobile
          comments={record.comments}
          patientGuid={record.patient.guid}
          erroneousAuthorName={record.erroneousAuthorName}
          erroneousMarkedAt={record.erroneousMarkedAt}
          erroneousReason={record.erroneousReason}
          isErroneous={record.isErroneous}
        />
      )}
      {showCommentColumn && (
        <div className={s['comment-mob-wrapper']}>
          <CommentColumn
            addComment={addActivityComment}
            deviceId={record.deviceId}
            readingId={record.id}
            idx={idx}
            key={`comment-col-${idx}`}
            createdAt={record.createdAt}
            showAiButton={showReviewCol}
            currentInteractionStatus={record.patient.interactionStatus}
            patientGuid={record.patient.guid}
            genAiComment={genAiComment}
            genAiCommentLoading={isLoading}
            isCommentGenerationLimitReached={isCommentGenerationLimitReached}
            isErroneous={record.isErroneous}
            thresholds={record.patient.threshold}
            values={record.value}
            statuses={record.statuses}
            readingType={record.readingType}
            globalStatus={record.globalStatus}
            thresholdName={record.thresholdName}
          />
        </div>
      )}
      {InteractionStatusAlert}
    </div>
  );
};

export default MobileCell;
